/**
 * Pane widths and collapse state (`110-design-system.md` §5).
 *
 * localStorage, not the server: this is per-device UI trivia, it is ~20 bytes, and it
 * must be applied before first paint. Same boundary as the theme
 * (`112-offline-drafts.md` §2). It holds no credentials — the session is an httpOnly
 * cookie and the CSRF token is in memory (`050-auth.md`), and neither ever comes near
 * this file.
 *
 * | Pane   | Default | Min | Max | Collapsible          |
 * |--------|---------|-----|-----|----------------------|
 * | Sidebar| 260px   | 180 | 480 | yes (Mod+\)          |
 * | Main   | flex-1  | 480 | —   | no — collapsing the  |
 * |        |         |     |     | content is not a     |
 * |        |         |     |     | thing anyone wants   |
 * | Right  | 360px   | 280 | 640 | yes, closed by default (Mod+.) |
 */
import { useCallback, useState } from 'react';

export type PaneId = 'sidebar' | 'right';

export interface PaneState {
  /** Pixels. The panel library is configured in px, exactly as §5's table is written. */
  size: number;
  collapsed: boolean;
}

export const PANE_LIMITS = {
  sidebar: { min: 180, max: 480, default: 260 },
  right: { min: 280, max: 640, default: 360 },
  main: { min: 480 },
} as const;

const STORAGE_KEYS: Record<PaneId, string> = {
  sidebar: 'rn.layout.sidebar',
  right: 'rn.layout.right',
};

const DEFAULTS: Record<PaneId, PaneState> = {
  sidebar: { size: PANE_LIMITS.sidebar.default, collapsed: false },
  right: { size: PANE_LIMITS.right.default, collapsed: true },
};

function clamp(pane: PaneId, size: number): number {
  const { min, max } = PANE_LIMITS[pane];
  return Math.min(max, Math.max(min, size));
}

export function readPane(pane: PaneId): PaneState {
  const fallback = DEFAULTS[pane];
  const raw = localStorage.getItem(STORAGE_KEYS[pane]);
  if (raw === null) return fallback;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return fallback;

    const { size, collapsed } = parsed as Partial<PaneState>;
    return {
      size: typeof size === 'number' && Number.isFinite(size) ? clamp(pane, size) : fallback.size,
      collapsed: typeof collapsed === 'boolean' ? collapsed : fallback.collapsed,
    };
  } catch {
    return fallback;
  }
}

function writePane(pane: PaneId, state: PaneState): void {
  localStorage.setItem(STORAGE_KEYS[pane], JSON.stringify(state));
}

/**
 * The pane's state, persisted on every change. The panel library owns the DOM; this
 * owns what survives a reload (criterion #8: "the state survives a reload").
 */
export function usePane(pane: PaneId) {
  const [state, setState] = useState<PaneState>(() => readPane(pane));

  const update = useCallback(
    (patch: Partial<PaneState>) => {
      setState((previous) => {
        const next: PaneState = { ...previous, ...patch };
        if (next.size === previous.size && next.collapsed === previous.collapsed) {
          return previous;
        }
        writePane(pane, next);
        return next;
      });
    },
    [pane],
  );

  return [state, update] as const;
}
